import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE, fetchApiJson } from "../config/apiBase";
import { useI18n } from "../i18n/I18nContext";
import PageTitle from "./include/PageTitle";

const categoryClassMap = {
  Property: "filter_category_1",
  Team: "filter_category_2",
  Event: "filter_category_3",
};

const galleryCopy = {
  en: {
    all: "all",
    loading: "Loading gallery...",
    empty: "No gallery items have been published yet.",
    imageAlt: "Gallery item",
    closeImage: "Close gallery image",
    previousImage: "Previous image",
    nextImage: "Next image",
    of: "of",
    categories: {
      Property: "Property",
      Team: "Team",
      Event: "Event",
    },
  },
  ar: {
    all: "الكل",
    loading: "جاري تحميل المعرض...",
    empty: "لا توجد عناصر منشورة في المعرض حتى الآن.",
    imageAlt: "عنصر من المعرض",
    closeImage: "إغلاق صورة المعرض",
    previousImage: "الصورة السابقة",
    nextImage: "الصورة التالية",
    of: "من",
    categories: {
      Property: "العقارات",
      Team: "الفريق",
      Event: "الفعاليات",
    },
  },
};

const fallbackImage = "/img/property-placeholder.svg";

const resolveGalleryImage = (url) => {
  if (!url) return fallbackImage;
  if (/^data:image\//i.test(url)) return url;
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/uploads\//i.test(url)) {
    return `${API_BASE}${new URL(url).pathname}`;
  }
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/")) return `${API_BASE}${url}`;
  return url.startsWith("/") ? url : `/${url}`;
};

const normalizeGalleryCategory = (category) => {
  if (category === "Team") return "Team";
  if (category === "Event") return "Event";
  return "Property";
};

const getGalleryImages = (item) =>
  (Array.isArray(item?.imageUrls) && item.imageUrls.length ? item.imageUrls : [item?.coverImageUrl])
    .map(resolveGalleryImage)
    .filter(Boolean);

const galleryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: "30px",
  alignItems: "start",
};

const galleryImageFrameStyle = {
  width: "100%",
  aspectRatio: "4 / 3",
  height: "auto",
  overflow: "hidden",
  background: "#eef2f3",
};

const galleryImageLinkStyle = {
  display: "block",
  width: "100%",
  height: "100%",
};

const galleryCardImageStyle = {
  display: "block",
  width: "100%",
  height: "100%",
  maxWidth: "none",
  objectFit: "cover",
  objectPosition: "center center",
};

const Gallery = () => {
  const { locale } = useI18n();
  const ui = galleryCopy[locale] || galleryCopy.en;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeGallery, setActiveGallery] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState("*");

  useEffect(() => {
    let ignore = false;

    const loadGallery = async () => {
      try {
        setLoading(true);
        setError("");
        const { json } = await fetchApiJson(`/api/gallery?lang=${locale}`);
        if (!ignore) setItems(Array.isArray(json?.data) ? json.data : []);
      } catch (err) {
        if (!ignore) {
          setItems([]);
          setError(err?.message || "Failed to load gallery.");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadGallery();
    return () => {
      ignore = true;
    };
  }, [locale]);

  const categories = useMemo(() => ["Property", "Event", "Team"], []);

  const filteredItems = useMemo(
    () => (activeCategory === "*" ? items : items.filter((item) => normalizeGalleryCategory(item.category) === activeCategory)),
    [activeCategory, items]
  );

  const activeImages = useMemo(() => (activeGallery ? getGalleryImages(activeGallery) : []), [activeGallery]);
  const activeImage = activeImages[activeImageIndex] || "";

  const openGalleryLightbox = (event, item, imageIndex = 0) => {
    event.preventDefault();
    setActiveGallery(item);
    setActiveImageIndex(imageIndex);
  };

  const closeGalleryLightbox = () => {
    setActiveGallery(null);
    setActiveImageIndex(0);
  };

  const showPreviousImage = useCallback(() => {
    if (!activeImages.length) return;
    setActiveImageIndex((index) => (index - 1 + activeImages.length) % activeImages.length);
  }, [activeImages.length]);

  const showNextImage = useCallback(() => {
    if (!activeImages.length) return;
    setActiveImageIndex((index) => (index + 1) % activeImages.length);
  }, [activeImages.length]);

  useEffect(() => {
    if (!activeGallery) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeGalleryLightbox();
      if (event.key === "ArrowLeft") showPreviousImage();
      if (event.key === "ArrowRight") showNextImage();
    };

    document.body.classList.add("qtech-gallery-modal-open");
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("qtech-gallery-modal-open");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeGallery, activeImages.length, showNextImage, showPreviousImage]);

  return (
    <div>
      <PageTitle
        titleKey="pages.gallery.title"
        currentPageKey="pages.gallery.title"
        bgImage="/img/service/gallery_banner_img.png"
        className="qtech-gallery-page-title"
      />

      <div className="ltn__gallery-area mb-120">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="ltn__gallery-menu">
                <div className="ltn__gallery-filter-menu portfolio-filter text-uppercase mb-50 qtech-gallery-filter-menu">
                  <button type="button" className={activeCategory === "*" ? "active" : ""} onClick={() => setActiveCategory("*")}>
                    {ui.all}
                  </button>
                  {categories.map((category) => (
                    <button
                      type="button"
                      key={category}
                      className={activeCategory === category ? "active" : ""}
                      onClick={() => setActiveCategory(category)}
                    >
                      {ui.categories[category] || category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center pt-40 pb-40">{ui.loading}</div>
          ) : null}

          {!loading && error ? (
            <div className="text-center pt-40 pb-40 text-danger">{error}</div>
          ) : null}

          {!loading && !error && items.length === 0 ? (
            <div className="text-center pt-40 pb-40">{ui.empty}</div>
          ) : null}

          {!loading && !error && items.length > 0 ? (
            <div className="qtech-gallery-grid ltn__gallery-style-1" style={galleryGridStyle}>
              {filteredItems.map((item) => {
                const images = getGalleryImages(item);
                const coverImage = item.coverImageUrl ? resolveGalleryImage(item.coverImageUrl) : images[0] || fallbackImage;
                const category = normalizeGalleryCategory(item.category);
                const categoryClass = categoryClassMap[category] || "filter_category_1";

                return (
                  <div className={`qtech-gallery-card ltn__gallery-item ${categoryClass}`} key={item.id}>
                    <div className="ltn__gallery-item-inner">
                      <div className="ltn__gallery-item-img" style={galleryImageFrameStyle}>
                        <a href={coverImage} onClick={(event) => openGalleryLightbox(event, item, 0)} style={galleryImageLinkStyle}>
                          <img
                            className="qtech-gallery-card-image"
                            src={coverImage}
                            alt={item.title || ui.imageAlt}
                            loading="lazy"
                            style={galleryCardImageStyle}
                          />
                          <span className="ltn__gallery-action-icon">
                            <i className="fas fa-search" />
                          </span>
                        </a>
                      </div>
                      <div className="ltn__gallery-item-info">
                        <h4>{item.title}</h4>
                        <p>{item.description || item.category}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
      {activeGallery ? (
        <div className="qtech-gallery-modal" role="dialog" aria-modal="true" aria-label={activeGallery.title || ui.imageAlt}>
          <button type="button" className="qtech-gallery-modal-backdrop" onClick={closeGalleryLightbox} aria-label={ui.closeImage} />
          <div className="qtech-gallery-modal-shell">
            <button type="button" className="qtech-gallery-modal-close" onClick={closeGalleryLightbox} aria-label={ui.closeImage}>
              <i className="fas fa-times" />
            </button>
            {activeImages.length > 1 ? (
              <button type="button" className="qtech-gallery-modal-arrow qtech-gallery-modal-arrow-prev" onClick={showPreviousImage} aria-label={ui.previousImage}>
                <i className="fas fa-chevron-left" />
              </button>
            ) : null}
            <div className="qtech-gallery-modal-image-wrap">
              <img src={activeImage} alt={activeGallery.title || ui.imageAlt} />
            </div>
            {activeImages.length > 1 ? (
              <button type="button" className="qtech-gallery-modal-arrow qtech-gallery-modal-arrow-next" onClick={showNextImage} aria-label={ui.nextImage}>
                <i className="fas fa-chevron-right" />
              </button>
            ) : null}
            <div className="qtech-gallery-modal-caption">
              <span>{activeImageIndex + 1} {ui.of} {activeImages.length}</span>
              <strong>{activeGallery.title}</strong>
              {activeGallery.description ? <p>{activeGallery.description}</p> : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Gallery;
